import type { VisitRow } from '@busy/api';

// Dashboard "Most Requested" tile — aggregates visits in the selected
// range into top-3 services + top-3 add-ons with count + revenue.
//
// Revenue attribution policy:
//   • Service revenue per visit = visit.price_cents − sum(addon prices)
//     so a "$50 cut + $5 hot towel = $55 visit" attributes $50 to the
//     cut and $5 to the add-on. Without this split, the add-on inflates
//     the cut's revenue and the line items don't reconcile to the
//     dashboard total.
//   • Add-on revenue = sum of priceCents on each add-on entry across
//     all visits where it appeared.
//
// Edge cases:
//   • Manual price-down adjustments could push service revenue negative
//     (price < addon total). Clamped to 0 — never surface a negative
//     "this cut earned -$5" on the tile, even if the math says so.
//   • Visits with no service_id AND empty cut_type drop out — nothing
//     to attribute them to.

export type TopMenuItem = {
  /** Visit's service_id when set; null for legacy / built-in matches. */
  serviceId: string | null;
  /** Lowercased normalized name — the canonical key used for grouping
   *  built-in catalog items whose visit row has cut_type but no FK. */
  normalizedName: string;
  /** Display name as captured on the most-recent visit — usually the
   *  catalog's localized name at the time the cut was logged. */
  displayName: string;
  count: number;
  revenueCents: number;
  /** Tip dollars attributed to this row. Always 0 for add-ons (tips
   *  attach to the SERVICE side of a visit by convention — the barber
   *  was tipped for the experience anchored by the cut, not split with
   *  add-ons). For services, this is the sum of tip_cents across every
   *  visit of that service. */
  tipCents: number;
  /** Tip rate as a 0-100 percent: tipCents / revenueCents. Lets the
   *  barber compare which services tip best across different price
   *  points — a $25 tip on a $50 cut (50%) and a $25 tip on a $100 cut
   *  (25%) tell very different stories about that service's economics.
   *  Null when revenue is 0 (denominator undefined) or when the row
   *  carries no tip attribution (add-ons). */
  tipRatePct: number | null;
};

export type TopMenuItemsAggregate = {
  services: TopMenuItem[];
  addons: TopMenuItem[];
  /** Total visits considered. Lets the tile surface "out of N cuts"
   *  context so a single dominant service doesn't read as "everyone
   *  asks for this." */
  totalVisits: number;
};

type AggregatorEntry = {
  serviceId: string | null;
  normalizedName: string;
  displayName: string;
  count: number;
  revenueCents: number;
  tipCents: number;
  lastVisitAt: string;
};

export function aggregateTopMenuItems(visits: VisitRow[]): TopMenuItemsAggregate {
  const services = new Map<string, AggregatorEntry>();
  const addons = new Map<string, AggregatorEntry>();

  for (const v of visits) {
    // Service side — pull the row's cut_type for display, normalize for
    // grouping. service_id is preferred as the bucket key but a normalized
    // name fallback covers historical rows where the FK is null.
    const sDisplayRaw = (v.cut_type ?? '').trim();
    const sNormalized = sDisplayRaw.toLowerCase();
    const sKey = v.service_id ?? (sNormalized ? `name:${sNormalized}` : null);

    // Compute the share of visit revenue that goes to the service (not
    // to add-ons). Negative-after-subtraction clamps to 0 — see header.
    const addonTotalCents = (v.addons ?? []).reduce(
      (sum, a) => sum + (a.priceCents ?? 0),
      0,
    );
    const serviceRevenueCents = Math.max(0, (v.price_cents ?? 0) - addonTotalCents);

    if (sKey) {
      // Tips attach to the service side of a visit by convention —
      // the barber was tipped for the experience, anchored by the cut.
      const visitTipCents = v.tip_cents ?? 0;
      const existing = services.get(sKey);
      if (existing) {
        existing.count += 1;
        existing.revenueCents += serviceRevenueCents;
        existing.tipCents += visitTipCents;
        if (v.started_at > existing.lastVisitAt) {
          existing.lastVisitAt = v.started_at;
          existing.displayName = sDisplayRaw || existing.displayName;
        }
      } else {
        services.set(sKey, {
          serviceId: v.service_id ?? null,
          normalizedName: sNormalized,
          displayName: sDisplayRaw,
          count: 1,
          revenueCents: serviceRevenueCents,
          tipCents: visitTipCents,
          lastVisitAt: v.started_at,
        });
      }
    }

    // Add-on side — every entry in visit.addons gets its own count + revenue.
    for (const a of v.addons ?? []) {
      const aDisplayRaw = (a.name ?? '').trim();
      const aNormalized = aDisplayRaw.toLowerCase();
      const aKey = a.id ?? (aNormalized ? `name:${aNormalized}` : null);
      if (!aKey) continue;
      const existing = addons.get(aKey);
      if (existing) {
        existing.count += 1;
        existing.revenueCents += a.priceCents ?? 0;
        if (v.started_at > existing.lastVisitAt) {
          existing.lastVisitAt = v.started_at;
          existing.displayName = aDisplayRaw || existing.displayName;
        }
      } else {
        addons.set(aKey, {
          serviceId: a.id ?? null,
          normalizedName: aNormalized,
          displayName: aDisplayRaw,
          count: 1,
          revenueCents: a.priceCents ?? 0,
          // Add-ons don't carry tip attribution — see TopMenuItem doc
          // on the tipCents field for the policy.
          tipCents: 0,
          lastVisitAt: v.started_at,
        });
      }
    }
  }

  // Sort by count desc, revenue desc as tie-breaker (more lucrative
  // service wins on a tie), recency desc as third tie-breaker so the
  // currently-popular item beats a long-tail one with stale activity.
  function pickTop(map: Map<string, AggregatorEntry>): TopMenuItem[] {
    return [...map.values()]
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        if (b.revenueCents !== a.revenueCents) return b.revenueCents - a.revenueCents;
        return b.lastVisitAt.localeCompare(a.lastVisitAt);
      })
      .slice(0, 3)
      .map(({ serviceId, normalizedName, displayName, count, revenueCents, tipCents }) => ({
        serviceId,
        normalizedName,
        displayName,
        count,
        revenueCents,
        tipCents,
        // tipRatePct = tipCents / revenueCents × 100. Null when there
        // are no tips OR no revenue (add-ons land here too, since
        // their tipCents is always 0 per the attribution policy).
        tipRatePct:
          tipCents > 0 && revenueCents > 0
            ? Math.round((tipCents / revenueCents) * 100)
            : null,
      }));
  }

  return {
    services: pickTop(services),
    addons: pickTop(addons),
    totalVisits: visits.length,
  };
}
